import { Link } from "react-router-dom";
import {
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Youtube,
  MapPin,
  Phone,
  Mail,
  Clock,
} from "lucide-react";
import { BRAND, DIVISIONS, LOGOS } from "../../lib/site-data";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0a3d7a] text-[#faf6f0] relative overflow-hidden"
    >
      {/* Decorative wave */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#f59e0b]/50 to-transparent" />

      <div className="container-x py-20 md:py-28">
        {/* Headline */}
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <img
              src={LOGOS.brandWhite}
              alt="Ocean Oasis"
              data-testid="footer-brand-logo"
              className="h-20 md:h-24 w-auto object-contain mb-8 opacity-95"
            />
            <p className="font-label text-[#f59e0b]">The Ocean Oasis Group</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-tight">
              Three divisions. One standard of excellence — rooted in Mogadishu,
              reaching the world.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3">
            <p className="text-[#faf6f0]/75 leading-relaxed font-body text-base">
              Ocean Oasis is a Somali-owned conglomerate operating premium
              retail, international logistics and hospitality experiences from
              the heart of Mogadishu. Built on reliability, integrity and a
              commitment to elevating the everyday.
            </p>
          </div>
        </div>

        <div className="hairline opacity-30 mb-14" style={{ background: "rgba(250,246,240,0.15)" }} />

        {/* Columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Divisions */}
          <div>
            <p className="font-label text-[#f59e0b] mb-5">Divisions</p>
            <ul className="space-y-3">
              {DIVISIONS.map((d) => (
                <li key={d.slug}>
                  <Link
                    to={d.href}
                    data-testid={`footer-division-${d.slug}`}
                    className="font-display text-xl hover:text-[#f59e0b] transition-colors"
                  >
                    {d.short}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className="font-label text-[#f59e0b] mb-5">Company</p>
            <ul className="space-y-3 font-body text-sm">
              <li>
                <Link
                  to="/about"
                  data-testid="footer-link-about"
                  className="hover:text-[#f59e0b] transition-colors"
                >
                  About Ocean Oasis
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-link-contact"
                  className="hover:text-[#f59e0b] transition-colors"
                >
                  Visit & Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies-policy"
                  data-testid="footer-link-cookies"
                  className="hover:text-[#f59e0b] transition-colors"
                >
                  Cookies Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  data-testid="footer-link-privacy"
                  className="hover:text-[#f59e0b] transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  data-testid="footer-link-terms"
                  className="hover:text-[#f59e0b] transition-colors"
                >
                  Terms of Use
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="font-label text-[#f59e0b] mb-5">Visit</p>
            <ul className="space-y-3 font-body text-sm text-[#faf6f0]/85">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-1 shrink-0 text-[#f59e0b]" />
                <span>
                  {BRAND.address.line1}, {BRAND.address.line2}
                  <br />
                  {BRAND.address.city}, {BRAND.address.country}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-1 shrink-0 text-[#f59e0b]" />
                <a
                  href={`tel:${BRAND.contact.phone.replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  className="hover:text-[#f59e0b]"
                >
                  {BRAND.contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-1 shrink-0 text-[#f59e0b]" />
                <a
                  href={`mailto:${BRAND.contact.email}`}
                  data-testid="footer-email"
                  className="hover:text-[#f59e0b]"
                >
                  {BRAND.contact.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={16} className="mt-1 shrink-0 text-[#f59e0b]" />
                <span>
                  <strong className="text-[#faf6f0] font-semibold">Supermarket &amp; Coffee</strong>
                  <br />
                  Every day · 7:00 AM – 10:00 PM
                  <br />
                  <span className="opacity-70">Logistics office: {BRAND.contact.hoursLogistics}</span>
                </span>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <p className="font-label text-[#f59e0b] mb-5">Follow</p>
            <div className="flex flex-wrap gap-3">
              {[
                { Icon: Instagram, key: "instagram" },
                { Icon: Facebook, key: "facebook" },
                { Icon: Linkedin, key: "linkedin" },
                { Icon: Twitter, key: "twitter" },
                { Icon: Youtube, key: "youtube" },
              ].map(({ Icon, key }) => (
                <a
                  key={key}
                  href={BRAND.social[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${key}`}
                  aria-label={key}
                  className="w-10 h-10 inline-flex items-center justify-center border border-[#faf6f0]/30 text-[#faf6f0] hover:bg-[#f59e0b] hover:text-[#0a3d7a] hover:border-[#f59e0b] transition-all duration-300"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="hairline opacity-30 mt-16" style={{ background: "rgba(250,246,240,0.15)" }} />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-8 font-body text-xs text-[#faf6f0]/60">
          <p>© {new Date().getFullYear()} Ocean Oasis Group · {BRAND.contact.domain}. All rights reserved.</p>
          <p className="tracking-[0.2em] uppercase">
            Mogadishu · Sourced Worldwide
          </p>
        </div>
      </div>
    </footer>
  );
}
